import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Globe, Gem, ShoppingBag, Truck, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import landingimg from "../assets/landingimg.jpg";

export const Landing = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8" data-testid="hero-section">
            <div className="inline-block border border-primary px-4 py-2">
              <span className="text-xs uppercase tracking-widest text-primary font-body font-medium">Direct from India</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight">
              Premium Natural Diamonds from Manufacturing Excellence
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed font-body">
              Precision diamond manufacturing from India's finest facilities. Exceptional quality, competitive pricing, and trusted service for jewelry manufacturers worldwide.
            </p>
            <div className="flex flex-wrap gap-4">
              {isAuthenticated ? (
                <Link 
                  to="/catalog" 
                  className="bg-primary text-white hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 transition-all duration-300 inline-flex items-center gap-2"
                  data-testid="hero-catalog-btn"
                >
                  Browse Catalog
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <>
                  <Link 
                    to="/register" 
                    className="bg-primary text-white hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 transition-all duration-300"
                    data-testid="hero-register-btn"
                  >
                    Register Now
                  </Link>
                  <Link 
                    to="/catalog" 
                    className="bg-transparent border border-primary text-primary hover:bg-primary hover:text-white uppercase tracking-widest text-xs font-bold px-8 py-4 transition-all duration-300"
                    data-testid="hero-browse-btn"
                  >
                    Browse Catalog
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="relative">
            <img 
              src={landingimg}
              alt="Premium Diamond"
              className="w-full h-auto"
            />
            <div className="absolute -bottom-6 -left-6 bg-secondary text-white p-6 border border-primary">
              <div className="flex items-center gap-3">
                <Award className="text-primary" size={32} />
                <div>
                  <div className="font-heading text-2xl font-bold">Certified</div>
                  <div className="text-xs uppercase tracking-widest text-white/80">GIA & IGI</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-surface noise-overlay" data-testid="services-section">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-4">Our Services</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Beyond traditional diamond manufacturing, we offer comprehensive support to help your business thrive.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white border border-border/50 p-8 hover:border-primary/50 transition-colors">
            <Gem className="text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold mb-3">Precision Manufacturing</h3>
            <p className="text-muted-foreground leading-relaxed">
              State-of-the-art diamond manufacturing facilities in India with exceptional quality control and craftsmanship.
            </p>
          </div>

          <div className="bg-white border border-border/50 p-8 hover:border-primary/50 transition-colors">
            <Globe className="text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold mb-3">Online Inventory Platform</h3>
            <p className="text-muted-foreground leading-relaxed">
              Exclusive online inventory system designed specifically for our platform, providing real-time access to our complete collection.
            </p>
          </div>

          <div className="bg-white border border-border/50 p-8 hover:border-primary/50 transition-colors">
            <ShoppingBag className="text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold mb-3">Custom Sourcing</h3>
            <p className="text-muted-foreground leading-relaxed">
              Leverage our global diamond network to source exactly what your clients need with precision.
            </p>
          </div>

          <div className="bg-white border border-border/50 p-8 hover:border-primary/50 transition-colors">
            <Truck className="text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold mb-3">Free Shipping</h3>
            <p className="text-muted-foreground leading-relaxed">
              Complimentary worldwide shipping on all invoices exceeding $15,000.
            </p>
          </div>

          <div className="bg-white border border-border/50 p-8 hover:border-primary/50 transition-colors">
            <Shield className="text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold mb-3">High-Res Media</h3>
            <p className="text-muted-foreground leading-relaxed">
              Professional images and videos for your online sales and marketing campaigns.
            </p>
          </div>

          <div className="bg-white border border-border/50 p-8 hover:border-primary/50 transition-colors">
            <Gem className="text-primary mb-4" size={32} />
            <h3 className="font-heading text-xl font-semibold mb-3">Custom Cutting</h3>
            <p className="text-muted-foreground leading-relaxed">
              Custom-cut natural diamonds crafted from your specific design drawings with manufacturing expertise.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="about-section">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">About Just Inn Diam</h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Founded by <span className="font-semibold text-foreground">Mr. Vatsal Jasani</span>, Just Inn Diam has evolved from a diamond manufacturer into a trusted global partner. We specialize in rare antique cuts, bold modern designs, and custom-cut natural diamonds ranging from 0.20 ct to 15 ct and beyond, all crafted with manufacturing excellence.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8">
            <div className="space-y-2">
              <div className="font-heading text-3xl font-bold text-primary">India</div>
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Manufacturing HQ</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-3xl font-bold text-primary">USA</div>
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Office</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-3xl font-bold text-primary">HK</div>
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Hong Kong</div>
            </div>
            <div className="space-y-2">
              <div className="font-heading text-3xl font-bold text-primary">Dubai</div>
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Office</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-secondary text-white noise-overlay" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">Ready to Source Premium Diamonds?</h2>
          <p className="text-lg text-white/80">
            Join jewelry brands worldwide who trust Just Inn Diam for quality, precision manufacturing, and exceptional service.
          </p>
          {!isAuthenticated && (
            <Link 
              to="/register" 
              className="bg-primary text-white hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-10 py-5 transition-all duration-300 inline-block"
              data-testid="cta-register-btn"
            >
              Create Your Account
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};